import {
  confidentText,
  type HealthCardReading,
  healthCardNumberOnly,
  healthCardReadingComplete,
  mergeHealthCardReadings,
  parseHealthCardText,
} from "./healthCardReader";
import {
  HEALTH_CARD_READER_BASE,
  HEALTH_CARD_READER_LANGUAGE,
  HEALTH_CARD_READER_WORKER,
} from "./healthCardReaderAssets";

/**
 * Reads the front of a health card on the patient's own phone (owner decision, 2026-10-10):
 * tesseract.js, a WebAssembly build of the Tesseract OCR engine, in a worker started from this
 * origin's /ocr/ files (healthCardReaderAssets.ts). Nothing new leaves the phone: no CDN, no
 * service, and the text is never logged, stored, cached or sent. Only the values it fills in
 * (healthCardReader.ts) are submitted, with the rest of the answers, once the patient has
 * checked them.
 *
 * The engine (a 4 MB core and a 3 MB model) is fetched only when a photo is to be read, never
 * on page load. Every reading gets its own worker, which is terminated when the reading ends,
 * fails, times out or is stopped, so its memory (the picture's copy and the model) goes too.
 */

/** A reading that has not finished by then is given up, and the patient types the details. */
export const HEALTH_CARD_READ_TIMEOUT_MS = 20_000;

/**
 * The reader works on a copy of the prepared photo enlarged to twice its size (at most this
 * long edge): a card's small dates read far better large. On synthetic cards the dates went
 * from rarely to nearly always read.
 */
const READ_LONG_EDGE_PX = 3200;
const READ_SCALE = 2;
const READ_JPEG_QUALITY = 0.92;

/**
 * The passes over one picture, in order, until the passes agree on everything a card shows (or
 * time runs out). Each is a page layout and a turn of the picture in radians, because a card
 * held a little crooked defeats the engine's own straightening: synthetic cards tilted up to
 * about 10 degrees read in one of these. The scattered-text layout jumbles the order of the
 * dates and their labels, so only its number and version code are kept.
 */
export const HEALTH_CARD_READ_PASSES: ReadonlyArray<{
  readonly layout: "block" | "sparse";
  readonly turn: number;
}> = [
  { layout: "block", turn: 0 },
  { layout: "sparse", turn: 0 },
  { layout: "block", turn: 0.1 },
  { layout: "block", turn: -0.1 },
  { layout: "block", turn: 0.18 },
  { layout: "block", turn: -0.18 },
];

/** Whether this browser can run the reader: a worker, WebAssembly and a canvas to enlarge on. */
export function healthCardReadingSupported(): boolean {
  return (
    typeof Worker === "function" &&
    typeof WebAssembly === "object" &&
    typeof WebAssembly.instantiate === "function" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.toBlob === "function"
  );
}

export interface ReadHealthCardOptions {
  /** Stops the reading (a new photo, the end of the session); the result is then null. */
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
}

type TesseractModule = typeof import("tesseract.js");

/** tesseract.js is CommonJS: depending on the bundler it arrives as the default export or not. */
async function loadTesseract(): Promise<TesseractModule> {
  const loaded: TesseractModule & { readonly default?: TesseractModule } = await import(
    "tesseract.js"
  );
  return loaded.default ?? loaded;
}

/** Settles like `promise`, or rejects as soon as `signal` aborts. */
function unlessAborted<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const stop = () => reject(signal.reason);
    signal.addEventListener("abort", stop, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", stop);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", stop);
        reject(error);
      },
    );
  });
}

/**
 * Runs `start` while recording every Worker it constructs. tesseract.js hands back its worker
 * only once the engine has loaded, so a reading stopped while loading could not otherwise end
 * it. The constructor is replaced only for this synchronous call (tesseract.js creates its
 * worker before its first await), then put back.
 */
function recordingWorkers<T>(started: Worker[], start: () => T): T {
  const NativeWorker = globalThis.Worker;
  globalThis.Worker = class RecordedWorker extends NativeWorker {
    public constructor(url: string | URL, options?: WorkerOptions) {
      super(url, options);
      started.push(this);
    }
  };
  try {
    return start();
  } finally {
    globalThis.Worker = NativeWorker;
  }
}

/** The picture enlarged for reading, as a JPEG; the canvas is freed at once. */
async function enlarged(picture: ImageBitmap): Promise<Blob> {
  const scale = Math.min(READ_SCALE, READ_LONG_EDGE_PX / Math.max(picture.width, picture.height));
  const canvas = document.createElement("canvas");
  try {
    canvas.width = Math.max(1, Math.round(picture.width * scale));
    canvas.height = Math.max(1, Math.round(picture.height * scale));
    const context = canvas.getContext("2d");
    if (context === null) throw new Error("No canvas");
    context.imageSmoothingQuality = "high";
    context.drawImage(picture, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", READ_JPEG_QUALITY);
    });
    if (blob === null) throw new Error("No picture");
    return blob;
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * Reads the prepared photo's picture (healthCardPhoto.ts: its preview) on this device. Resolves
 * to what an Ontario card showed, or null when it found no valid health number, failed or was
 * stopped. At the time limit it keeps what the passes so far agreed on. Never throws, never
 * logs, and always terminates its worker.
 */
export async function readHealthCardPhoto(
  picture: ImageBitmap,
  { signal, timeoutMs = HEALTH_CARD_READ_TIMEOUT_MS }: ReadHealthCardOptions = {},
): Promise<HealthCardReading | null> {
  if (!healthCardReadingSupported() || signal?.aborted) return null;
  const run = new AbortController();
  const stop = () => run.abort();
  signal?.addEventListener("abort", stop, { once: true });
  const timer = setTimeout(stop, timeoutMs);
  const started: Worker[] = [];
  const terminate = () => {
    for (const worker of started.splice(0)) worker.terminate();
  };
  run.signal.addEventListener("abort", terminate, { once: true });
  const readings: (HealthCardReading | null)[] = [];
  try {
    const image = await unlessAborted(enlarged(picture), run.signal);
    const tesseract = await unlessAborted(loadTesseract(), run.signal);
    const creating = recordingWorkers(started, () =>
      tesseract.createWorker(
        HEALTH_CARD_READER_LANGUAGE,
        tesseract.OEM.LSTM_ONLY,
        {
          workerPath: `${HEALTH_CARD_READER_BASE}${HEALTH_CARD_READER_WORKER}`,
          corePath: HEALTH_CARD_READER_BASE,
          langPath: HEALTH_CARD_READER_BASE,
          // A worker from this origin's URL rather than a blob: (the CSP allows no blob:).
          workerBlobURL: false,
          // Never keep the model in IndexedDB: nothing is saved on this device.
          cacheMethod: "none",
          gzip: true,
          logger: () => undefined,
          // Without a handler, tesseract.js throws its errors from the worker's message event.
          errorHandler: () => undefined,
        },
        // The dictionaries only get in the way of numbers and names.
        { load_system_dawg: "0", load_freq_dawg: "0" },
      ),
    );
    // A load that fails after the reading was given up must not surface as unhandled.
    creating.catch(() => undefined);
    const worker = await unlessAborted(creating, run.signal);
    for (const pass of HEALTH_CARD_READ_PASSES) {
      await unlessAborted(
        worker.setParameters({
          tessedit_pageseg_mode:
            pass.layout === "block" ? tesseract.PSM.SINGLE_BLOCK : tesseract.PSM.SPARSE_TEXT,
        }),
        run.signal,
      );
      const result = await unlessAborted(
        worker.recognize(image, { rotateRadians: pass.turn }, { text: false, blocks: true }),
        run.signal,
      );
      const reading = parseHealthCardText(confidentText(result.data.blocks ?? []));
      readings.push(pass.layout === "sparse" ? healthCardNumberOnly(reading) : reading);
      if (healthCardReadingComplete(mergeHealthCardReadings(readings))) break;
    }
    return mergeHealthCardReadings(readings);
  } catch {
    // Stopped by the patient or the session: nothing. Out of time: what the passes agreed on.
    return signal?.aborted === true ? null : mergeHealthCardReadings(readings);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", stop);
    run.signal.removeEventListener("abort", terminate);
    terminate();
  }
}
